import React, { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, ArrowLeft, ChevronRight } from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'

const ease = [0.16, 1, 0.3, 1] as const

const brand = {
  black: '#0A0B0B',
  white: '#F7F8F6',
  tapeTurquoise: '#0D3135',
  utilityBlue: '#145F6C',
  drillbitSkies: '#4A7D83',
  sandrift: '#AB9381',
  plaster: '#CECAC3',
} as const

type IndicatorStatus = 'green' | 'amber' | 'red'

interface QuizOption {
  label: string
  score: number
  status: IndicatorStatus
}

interface QuizStep {
  id: string
  question: string
  indicator: string
  options: QuizOption[]
  weight: number
  systemId: string
}

const STEPS: QuizStep[] = [
  {
    id: 'homeAge',
    question: 'When was your home built?',
    indicator: 'Foundation',
    systemId: 'foundation',
    weight: 15,
    options: [
      { label: '2020 or newer', score: 1.0, status: 'green' },
      { label: '2010 – 2019', score: 0.85, status: 'green' },
      { label: '2000 – 2009', score: 0.65, status: 'amber' },
      { label: '1980 – 1999', score: 0.45, status: 'amber' },
      { label: 'Before 1980', score: 0.25, status: 'red' },
    ],
  },
  {
    id: 'sqft',
    question: 'How large is your home?',
    indicator: 'Size',
    systemId: 'walls',
    weight: 10,
    options: [
      { label: 'Under 1,500 sq ft', score: 0.9, status: 'green' },
      { label: '1,500 – 2,500 sq ft', score: 0.85, status: 'green' },
      { label: '2,500 – 3,500 sq ft', score: 0.7, status: 'amber' },
      { label: '3,500 – 5,000 sq ft', score: 0.55, status: 'amber' },
      { label: '5,000+ sq ft', score: 0.4, status: 'red' },
    ],
  },
  {
    id: 'hvac',
    question: 'When was your HVAC system last serviced?',
    indicator: 'HVAC',
    systemId: 'chimney',
    weight: 20,
    options: [
      { label: 'This year', score: 1.0, status: 'green' },
      { label: 'Last year', score: 0.75, status: 'green' },
      { label: '2+ years ago', score: 0.4, status: 'amber' },
      { label: 'Not sure', score: 0.2, status: 'red' },
    ],
  },
  {
    id: 'waterHeater',
    question: 'How old is your water heater?',
    indicator: 'Water Heater',
    systemId: 'waterheater',
    weight: 15,
    options: [
      { label: 'Under 5 years', score: 1.0, status: 'green' },
      { label: '5 – 10 years', score: 0.65, status: 'amber' },
      { label: '10+ years', score: 0.3, status: 'red' },
      { label: 'No idea', score: 0.2, status: 'red' },
    ],
  },
  {
    id: 'roof',
    question: 'When was your roof last inspected?',
    indicator: 'Roof',
    systemId: 'roof',
    weight: 20,
    options: [
      { label: 'Within 2 years', score: 1.0, status: 'green' },
      { label: '3 – 5 years ago', score: 0.6, status: 'amber' },
      { label: '5+ years ago', score: 0.3, status: 'red' },
      { label: 'Never', score: 0.15, status: 'red' },
    ],
  },
  {
    id: 'gutters',
    question: 'When were your gutters last cleaned?',
    indicator: 'Gutters',
    systemId: 'gutters',
    weight: 10,
    options: [
      { label: 'This season', score: 1.0, status: 'green' },
      { label: 'Last season', score: 0.7, status: 'green' },
      { label: '1+ year ago', score: 0.35, status: 'amber' },
      { label: 'Not sure', score: 0.2, status: 'red' },
    ],
  },
  {
    id: 'safety',
    question: 'How old are your smoke and CO detectors?',
    indicator: 'Safety',
    systemId: 'safety',
    weight: 10,
    options: [
      { label: 'Under 5 years', score: 1.0, status: 'green' },
      { label: '5 – 10 years', score: 0.55, status: 'amber' },
      { label: '10+ years', score: 0.25, status: 'red' },
      { label: "I don't know", score: 0.2, status: 'red' },
    ],
  },
]

const TOTAL_STEPS = STEPS.length + 2 // quiz steps + contact + reveal

const STATUS_COLORS: Record<IndicatorStatus, string> = {
  green: '#38A169',
  amber: '#D69E2E',
  red: '#E53E3E',
}

// ============================================
// SVG HOUSE ILLUSTRATION
// ============================================
const HouseSVG: React.FC<{
  indicators: Record<string, { status: IndicatorStatus; label: string } | null>
}> = ({ indicators }) => (
  <svg viewBox="0 0 400 360" className="w-full max-w-[360px] mx-auto" aria-hidden>
    {/* Sky gradient */}
    <defs>
      <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={brand.drillbitSkies} stopOpacity="0.15" />
        <stop offset="100%" stopColor={brand.white} stopOpacity="0" />
      </linearGradient>
    </defs>
    <rect width="400" height="360" fill="url(#sky)" rx="12" />

    {/* Ground */}
    <rect x="0" y="290" width="400" height="70" fill={brand.plaster} opacity="0.5" rx="0" />

    {/* House body */}
    <rect id="house-walls" x="80" y="160" width="240" height="130" fill={brand.white} stroke={brand.tapeTurquoise} strokeWidth="2" />

    {/* Roof */}
    <polygon id="house-roof" points="60,160 200,70 340,160" fill={brand.tapeTurquoise} stroke={brand.tapeTurquoise} strokeWidth="2" />

    {/* Door */}
    <rect id="house-door" x="175" y="220" width="50" height="70" fill={brand.sandrift} rx="2" />
    <circle cx="218" cy="258" r="3" fill={brand.tapeTurquoise} />

    {/* Windows */}
    <rect x="105" y="195" width="45" height="40" fill={brand.drillbitSkies} opacity="0.3" stroke={brand.tapeTurquoise} strokeWidth="1.5" rx="1" />
    <rect x="250" y="195" width="45" height="40" fill={brand.drillbitSkies} opacity="0.3" stroke={brand.tapeTurquoise} strokeWidth="1.5" rx="1" />

    {/* Chimney */}
    <rect id="house-chimney" x="270" y="90" width="30" height="55" fill={brand.tapeTurquoise} />

    {/* Tree */}
    <rect x="355" y="230" width="10" height="60" fill={brand.sandrift} />
    <circle cx="360" cy="215" r="28" fill={brand.drillbitSkies} opacity="0.5" />

    {/* System indicators */}
    {indicators.chimney && (
      <g>
        <motion.circle
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          cx="285" cy="85" r="10"
          fill={STATUS_COLORS[indicators.chimney.status]}
        />
        <motion.text
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          x="302" y="89" fontSize="9" fill={brand.tapeTurquoise} fontFamily="sans-serif" fontWeight="600"
        >
          {indicators.chimney.label}
        </motion.text>
      </g>
    )}

    {indicators.roof && (
      <g>
        <motion.circle
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          cx="140" cy="120" r="10"
          fill={STATUS_COLORS[indicators.roof.status]}
        />
        <motion.text
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          x="155" y="124" fontSize="9" fill="#fff" fontFamily="sans-serif" fontWeight="600"
        >
          {indicators.roof.label}
        </motion.text>
      </g>
    )}

    {indicators.gutters && (
      <g>
        <motion.circle
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          cx="70" cy="158" r="10"
          fill={STATUS_COLORS[indicators.gutters.status]}
        />
        <motion.text
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          x="20" y="162" fontSize="9" fill={brand.tapeTurquoise} fontFamily="sans-serif" fontWeight="600"
        >
          {indicators.gutters.label}
        </motion.text>
      </g>
    )}

    {indicators.walls && (
      <g>
        <motion.circle
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          cx="330" cy="220" r="10"
          fill={STATUS_COLORS[indicators.walls.status]}
        />
        <motion.text
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          x="345" y="224" fontSize="9" fill={brand.tapeTurquoise} fontFamily="sans-serif" fontWeight="600"
        >
          {indicators.walls.label}
        </motion.text>
      </g>
    )}

    {indicators.foundation && (
      <g>
        <motion.circle
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          cx="80" cy="288" r="10"
          fill={STATUS_COLORS[indicators.foundation.status]}
        />
        <motion.text
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          x="10" y="292" fontSize="9" fill={brand.tapeTurquoise} fontFamily="sans-serif" fontWeight="600"
        >
          {indicators.foundation.label}
        </motion.text>
      </g>
    )}

    {indicators.waterheater && (
      <g>
        <motion.circle
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          cx="108" cy="260" r="10"
          fill={STATUS_COLORS[indicators.waterheater.status]}
        />
        <motion.text
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          x="20" y="264" fontSize="9" fill={brand.tapeTurquoise} fontFamily="sans-serif" fontWeight="600"
        >
          {indicators.waterheater.label}
        </motion.text>
      </g>
    )}

    {indicators.safety && (
      <g>
        <motion.circle
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          cx="200" cy="180" r="10"
          fill={STATUS_COLORS[indicators.safety.status]}
        />
        <motion.text
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          x="215" y="184" fontSize="9" fill={brand.tapeTurquoise} fontFamily="sans-serif" fontWeight="600"
        >
          {indicators.safety.label}
        </motion.text>
      </g>
    )}
  </svg>
)

// ============================================
// ANIMATED SCORE COUNTER
// ============================================
const AnimatedScore: React.FC<{ target: number }> = ({ target }) => {
  const [value, setValue] = useState(0)
  const frameRef = useRef(0)

  useEffect(() => {
    const duration = 2000
    const start = performance.now()

    const tick = (now: number) => {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(eased * target))
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick)
      }
    }

    frameRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameRef.current)
  }, [target])

  return <span>{value}</span>
}

// ============================================
// RISK ITEMS
// ============================================
interface RiskArea {
  label: string
  status: IndicatorStatus
  message: string
}

function getRiskAreas(answers: Record<string, number>): RiskArea[] {
  const areas: RiskArea[] = []

  const entries: { id: string; step: QuizStep; optIdx: number }[] = []
  STEPS.forEach((step) => {
    const optIdx = answers[step.id]
    if (optIdx !== undefined) {
      entries.push({ id: step.id, step, optIdx })
    }
  })

  entries.sort((a, b) => a.step.options[a.optIdx].score - b.step.options[b.optIdx].score)

  for (const { step, optIdx } of entries.slice(0, 3)) {
    const opt = step.options[optIdx]
    let msg = ''
    if (opt.status === 'red') msg = 'Needs immediate attention'
    else if (opt.status === 'amber') msg = 'Should be addressed soon'
    else msg = 'Looking good'
    areas.push({ label: step.indicator, status: opt.status, message: msg })
  }

  return areas
}

function calculateScore(answers: Record<string, number>): number {
  let total = 0
  STEPS.forEach((step) => {
    const optIdx = answers[step.id]
    if (optIdx !== undefined) {
      total += step.options[optIdx].score * step.weight
    }
  })
  return Math.round(total)
}

function getCostOfWaiting(score: number): string {
  if (score <= 40) return '$3,200 – $6,500'
  if (score <= 70) return '$1,200 – $3,200'
  return '$500 – $1,200'
}

// ============================================
// MAIN PAGE COMPONENT
// ============================================
const SnapshotPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0)
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [indicators, setIndicators] = useState<Record<string, { status: IndicatorStatus; label: string } | null>>({})
  const [contactInfo, setContactInfo] = useState({ name: '', email: '', phone: '', zip: '' })
  const [direction, setDirection] = useState(1)

  const totalSteps = TOTAL_STEPS
  const progressFraction = (currentStep + 1) / totalSteps

  const isQuizStep = currentStep < STEPS.length
  const isContactStep = currentStep === STEPS.length
  const isRevealStep = currentStep === STEPS.length + 1

  const handleSelectOption = useCallback(
    (optIdx: number) => {
      if (!isQuizStep) return
      const step = STEPS[currentStep]
      const opt = step.options[optIdx]

      setAnswers((prev) => ({ ...prev, [step.id]: optIdx }))
      setIndicators((prev) => ({
        ...prev,
        [step.systemId]: { status: opt.status, label: step.indicator },
      }))

      setTimeout(() => {
        setDirection(1)
        setCurrentStep((s) => s + 1)
      }, 350)
    },
    [currentStep, isQuizStep]
  )

  const handleBack = () => {
    if (currentStep > 0) {
      setDirection(-1)
      setCurrentStep((s) => s - 1)
    }
  }

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setDirection(1)
    setCurrentStep(STEPS.length + 1)
  }

  const score = calculateScore(answers)
  const risks = getRiskAreas(answers)

  const slideVariants = {
    enter: (d: number) => ({ opacity: 0, y: d > 0 ? 40 : -40 }),
    center: { opacity: 1, y: 0 },
    exit: (d: number) => ({ opacity: 0, y: d > 0 ? -20 : 20 }),
  }

  return (
    <div style={{ backgroundColor: brand.white }} className="min-h-screen">
      <MetaTags
        title="MyHome Snapshot | Free Home Health Score | Hudson My Home"
        description="Answer 7 quick questions to get your free MyHome Snapshot Score. See where your home stands and what needs attention first."
      />
      <Navbar />

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-[70] h-1" style={{ backgroundColor: brand.plaster }}>
        <motion.div
          className="h-full origin-left"
          style={{ backgroundColor: brand.sandrift }}
          animate={{ scaleX: progressFraction }}
          transition={{ duration: 0.4, ease }}
        />
      </div>

      <div className="pt-28 pb-20 md:pb-32">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center min-h-[70vh]">
            {/* Left: House visualization */}
            <div className="flex justify-center">
              <HouseSVG indicators={indicators} />
            </div>

            {/* Right: Question / Contact / Reveal */}
            <div className="relative">
              {!isRevealStep && (
                <p className="font-sans text-sm font-medium tracking-wide mb-8" style={{ color: brand.drillbitSkies }}>
                  {currentStep + 1} of {totalSteps}
                </p>
              )}

              <AnimatePresence mode="wait" custom={direction}>
                {isQuizStep && (
                  <motion.div
                    key={`quiz-${currentStep}`}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.5, ease }}
                  >
                    <h2
                      className="font-serif font-normal"
                      style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', lineHeight: 1.15, letterSpacing: '-0.02em', color: brand.black }}
                    >
                      {STEPS[currentStep].question}
                    </h2>

                    <div className="mt-8 flex flex-col gap-3">
                      {STEPS[currentStep].options.map((opt, idx) => (
                        <button
                          key={opt.label}
                          type="button"
                          onClick={() => handleSelectOption(idx)}
                          className="group w-full text-left px-6 py-4 border transition-all duration-300 font-sans text-base hover:shadow-md"
                          style={{
                            borderColor: answers[STEPS[currentStep].id] === idx ? brand.sandrift : brand.plaster,
                            backgroundColor: answers[STEPS[currentStep].id] === idx ? `${brand.sandrift}10` : 'transparent',
                          }}
                        >
                          <span className="flex items-center justify-between">
                            <span className="text-grey-900">{opt.label}</span>
                            <ChevronRight
                              className="w-4 h-4 opacity-0 group-hover:opacity-60 transition-opacity duration-200"
                              style={{ color: brand.tapeTurquoise }}
                            />
                          </span>
                        </button>
                      ))}
                    </div>

                    {currentStep > 0 && (
                      <button
                        type="button"
                        onClick={handleBack}
                        className="mt-6 inline-flex items-center gap-2 font-sans text-sm transition-opacity hover:opacity-70"
                        style={{ color: brand.drillbitSkies }}
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                      </button>
                    )}
                  </motion.div>
                )}

                {isContactStep && (
                  <motion.div
                    key="contact"
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.5, ease }}
                  >
                    <h2
                      className="font-serif font-normal"
                      style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', lineHeight: 1.15, letterSpacing: '-0.02em', color: brand.black }}
                    >
                      Your MyHome Snapshot is almost ready.
                    </h2>
                    <p className="mt-3 font-sans text-base text-grey-600">
                      Where should we send it?
                    </p>

                    <form onSubmit={handleContactSubmit} className="mt-8 flex flex-col gap-4">
                      <input
                        type="text"
                        required
                        placeholder="Full Name"
                        value={contactInfo.name}
                        onChange={(e) => setContactInfo((c) => ({ ...c, name: e.target.value }))}
                        className="w-full px-5 py-4 border font-sans text-base outline-none focus:ring-2 transition-shadow"
                        style={{ borderColor: brand.plaster, backgroundColor: '#fff' }}
                      />
                      <input
                        type="email"
                        required
                        placeholder="Email Address"
                        value={contactInfo.email}
                        onChange={(e) => setContactInfo((c) => ({ ...c, email: e.target.value }))}
                        className="w-full px-5 py-4 border font-sans text-base outline-none focus:ring-2 transition-shadow"
                        style={{ borderColor: brand.plaster, backgroundColor: '#fff' }}
                      />
                      <input
                        type="tel"
                        placeholder="Phone (optional)"
                        value={contactInfo.phone}
                        onChange={(e) => setContactInfo((c) => ({ ...c, phone: e.target.value }))}
                        className="w-full px-5 py-4 border font-sans text-base outline-none focus:ring-2 transition-shadow"
                        style={{ borderColor: brand.plaster, backgroundColor: '#fff' }}
                      />
                      <input
                        type="text"
                        required
                        placeholder="Zip Code"
                        value={contactInfo.zip}
                        onChange={(e) => setContactInfo((c) => ({ ...c, zip: e.target.value }))}
                        className="w-full px-5 py-4 border font-sans text-base outline-none focus:ring-2 transition-shadow"
                        style={{ borderColor: brand.plaster, backgroundColor: '#fff' }}
                      />
                      <button
                        type="submit"
                        className="mt-4 group inline-flex items-center justify-center gap-2 px-8 py-4 font-sans text-base font-semibold text-white transition-opacity hover:opacity-90"
                        style={{ backgroundColor: brand.sandrift }}
                      >
                        See My Score
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </form>

                    <button
                      type="button"
                      onClick={handleBack}
                      className="mt-6 inline-flex items-center gap-2 font-sans text-sm transition-opacity hover:opacity-70"
                      style={{ color: brand.drillbitSkies }}
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </button>
                  </motion.div>
                )}

                {isRevealStep && (
                  <motion.div
                    key="reveal"
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.5, ease }}
                  >
                    {/* Score */}
                    <div className="text-center lg:text-left">
                      <p className="font-sans text-sm font-semibold uppercase tracking-[0.15em] mb-3" style={{ color: brand.drillbitSkies }}>
                        Your MyHome Snapshot Score
                      </p>
                      <div
                        className="font-serif font-normal"
                        style={{ fontSize: 'clamp(4rem, 10vw, 7rem)', lineHeight: 1, letterSpacing: '-0.04em', color: brand.tapeTurquoise }}
                      >
                        <AnimatedScore target={score} />
                        <span className="text-3xl text-grey-400 ml-1">/100</span>
                      </div>

                      <p className="mt-6 font-sans text-base text-grey-700 max-w-lg">
                        {score <= 40
                          ? "Your home needs attention in several key areas. The good news: catching these now prevents costly surprises later."
                          : score <= 70
                            ? "Your home is in fair shape, but there are areas that could use proactive care before they become expensive problems."
                            : "Your home is in great shape. A few small improvements could help protect it for years to come."}
                      </p>
                    </div>

                    {/* Risk areas */}
                    <div className="mt-10 flex flex-col gap-4">
                      <p className="font-sans text-sm font-semibold uppercase tracking-[0.15em]" style={{ color: brand.drillbitSkies }}>
                        Priority Areas
                      </p>
                      {risks.map((risk, i) => (
                        <motion.div
                          key={risk.label}
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, ease, delay: 1.5 + i * 0.15 }}
                          className="flex items-center gap-4 px-5 py-4 border"
                          style={{ borderColor: brand.plaster }}
                        >
                          <div
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: STATUS_COLORS[risk.status] }}
                          />
                          <div>
                            <p className="font-sans text-base font-medium text-grey-900">{risk.label}</p>
                            <p className="font-sans text-sm text-grey-500">{risk.message}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    {/* Cost of waiting */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, ease, delay: 2.0 }}
                      className="mt-8 px-6 py-5 border-l-4"
                      style={{ borderColor: brand.sandrift, backgroundColor: `${brand.plaster}40` }}
                    >
                      <p className="font-sans text-sm text-grey-700">
                        Homeowners with a score of <strong>{score}</strong> typically spend{' '}
                        <strong style={{ color: brand.sandrift }}>{getCostOfWaiting(score)}</strong>{' '}
                        more per year on reactive repairs.
                      </p>
                    </motion.div>

                    {/* CTA */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7, ease, delay: 2.5 }}
                      className="mt-10"
                    >
                      <a
                        href="https://api.leadconnectorhq.com/widget/booking/vGBU75grNpdx4TI2SvX2"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-2 px-10 py-5 font-sans text-base font-semibold text-white transition-opacity hover:opacity-90"
                        style={{ backgroundColor: brand.sandrift }}
                      >
                        Walk Through Your Results with a Hudson
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </a>
                      <p className="mt-3 font-sans text-sm text-grey-500">
                        Free, 20 minutes, no obligation.
                      </p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default SnapshotPage
